/* eslint-disable max-lines */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-danger */
import React, {
    useContext, useMemo, useEffect, useState,
} from "react";
import ProductReviews from "components/product/reviews";
import AffirmPayments from "components/affirm-payments";
import navigate from "@helpers/link-navigate";
import useImageGallery from "hooks/useImageGallery";
import useProduct from "hooks/useProduct";
import { CartContext } from "context/cart";
import ShopperApproveCard from "@root/components/shopper-approve-card";
import GoogleReviewsCard from "@root/components/google-reviews-card";
import {
    mapItems,
    trackAddToCart, trackQuantityChange, trackSliderClick, trackViewItem,
} from "analytics";
import YoutubeFrame from "components/youtube-frame";
import convertToEmbed from "helpers/youtube-embedded";
import StoreReviews from "components/store-reviews";
import getProductAttributes from "helpers/get-product-attributes";
import NProgress from "nprogress";
import PatioCalculator from "context/patio-calculator";
import extractGid from "helpers/extract-gid";
/* import RenderModules from "helpers/render-modules"; */
import Breadcrumbs from "@_components/breadcrumbs";
import hasContainer from "@root/helpers/has-container";
import { CategoryContext } from "@root/context/category";
import { FormValidateContext } from "@root/context/form-validate";
import useIsMobile from "@root/hooks/useIsMobile";
import { useLocation } from "@reach/router";
import ProductDetails from "./details";
import Patio from "./patio";
import ProductSummary from "../product-summary";
import { getReviewCounterForCustomProducts } from "utils/review-counter";
import ImageGalleryPatio from "../../_components/image-gallery";
import ImageGallery from "../image-gallery";
import CTA from "../../_components/cta";
import Tabs from "../../_components/tabs";
import { badgesByVehicles, manualReviews } from "../collection/data";
import ParsedContent from "./parsed-content";
import ProductSummaryHorizontal from "../product-summary-horizontal";
import CausewayBridge from "components/causeway-bridge";
import filterImagesByVariant from "helpers/filter-images-by-variant";
import filterImagesByColor from "helpers/filter-images-by-color";

const LoadingContainer = () => (
    <div className="container">
        <div className="container--other-pages">
            <h2 className="text-blue-700"> Loading Product... </h2>
        </div>
    </div>
);

const Details = ({ isPatio, product, handleVariantChange, quantity, handleQuantityChange, handleAddToCart, patioConfig, variantColors, version }) => {
    if (isPatio) {
        return (
            <Patio
                product={product}
                onVariantChange={handleVariantChange}
                quantity={quantity}
                handleQuantityChange={handleQuantityChange}
                onAddToCart={handleAddToCart}
                patioConfig={patioConfig}
                variantColors={variantColors}
            />
        );
    }
    return (
        <ProductDetails
            product={product}
            onVariantChange={handleVariantChange}
            quantity={quantity}
            handleQuantityChange={handleQuantityChange}
            onAddToCart={handleAddToCart}
            variantColors={variantColors}
            version={version}
        />
    );
};

export function getSelectedOption(variant, option) {
    return variant?.selectedOptions?.find((selectedOption) => selectedOption.name === option)?.value;
}

const Product = ({ productData, patioConfig, variantColors, categorySlugMap }) => {
    const categories = productData?.product.categories || [];

    const currentLocation = useLocation();

    const breadcrumbItems = useMemo(() => {
        if (productData) {
            return [
                { title: "Home", url: "/" },
                ...categories.map((category) => ({
                    title: category.name,
                    url: categorySlugMap?.[category?.slug] ?? `${categorySlugMap?.[categories[0]?.slug]}/${category.slug}`,
                })),
                { title: productData?.product?.title, url: `/products/${productData?.product?.handle}` },
            ];
        }
        return [];
    }, [productData, categories, categorySlugMap]);

    if (!productData?.product) return <LoadingContainer />;

    const isPatio = productData?.product?.variants?.edges[0]?.node?.dynamic_price;

    const editData = currentLocation.state?.editData || {};
    const isEditing = editData.isEditing || false;
    const item = editData.item || null;
    const initialConfiguration = editData.configuration || {};
    const initialCustomAttributes = editData.customAttributes || {};

    // Preset variant: from edit flow, URL params (fabric & color), or default
    let presetVariantId;
    if (initialConfiguration?.variant) {
        presetVariantId = +initialConfiguration.variant;
    } else if (isPatio && productData?.product?.variants?.edges) {
        const urlParams = new URLSearchParams(currentLocation?.search || "");
        const fabricParam = urlParams.get("fabric");
        const colorParam = urlParams.get("color");
        const variants = productData.product.variants.edges;

        if (fabricParam) {
            let matchingVariant;
            if (colorParam) {
                matchingVariant = variants.find((variant) => {
                    const fabric = getSelectedOption(variant?.node, "Fabric");
                    const color = getSelectedOption(variant?.node, "Color");
                    return fabric?.toLowerCase() === fabricParam.toLowerCase()
                        && color?.toLowerCase() === colorParam.toLowerCase();
                });
            } else {
                // Only fabric: prefer Gray (default color), otherwise first variant with that fabric
                matchingVariant = variants.find((variant) => {
                    const fabric = getSelectedOption(variant?.node, "Fabric");
                    const color = getSelectedOption(variant?.node, "Color");
                    return fabric?.toLowerCase() === fabricParam.toLowerCase()
                        && color?.toLowerCase() === "gray";
                }) || variants.find((variant) => {
                    const fabric = getSelectedOption(variant?.node, "Fabric");
                    return fabric?.toLowerCase() === fabricParam.toLowerCase();
                });
            }
            if (matchingVariant) {
                presetVariantId = +extractGid(matchingVariant.node.id);
            }
        }
    }

    const { validateForm } = useContext(FormValidateContext) || {};

    const editingQuantity = isEditing && editData.configuration.quantity ? editData.configuration.quantity : 1;

    const [quantity, setQuantity] = useState(editingQuantity);
    const { thumbsSlider, slideshowSlider, setThumbsSlider, setSlideshowSlider, slideTo } = useImageGallery();
    const { product, getVariantImageIndex, setVariant, getVariantByOptions } = useProduct(productData?.product, presetVariantId, isPatio);
    const { addCartItem, removeCartItem } = useContext(CartContext);

    const video = !isPatio && product?.metafields?.edges?.filter((metafield) => metafield.node.key === "full_video");
    const initialGallerySlide = (productData && productData.queryVariant && getVariantImageIndex(product.currentVariant) !== -1) ? getVariantImageIndex(product.currentVariant) : 0;

    const defaultCustomizations = product?.customizations?.map((customization) => customization?.default_variant_id).filter((ids) => ids);
    const defaultParameters = {};

    product?.measurements?.forEach((customization) => {
        defaultParameters[customization.key] = customization.default;
    });

    const isGroupedImages = !isPatio && product?.is_grouped_images;
    const shouldFilterImagesByColor = isPatio || isGroupedImages;

    useEffect(() => {
        if (!shouldFilterImagesByColor) return;
        setTimeout(() => slideTo(isPatio ? 1 : 0), 300);
    }, [shouldFilterImagesByColor, isPatio, getSelectedOption(product.currentVariant.node, "Fabric"), getSelectedOption(product.currentVariant.node, "Color")]);

    const slideToSelectedVariantImage = async (variant) => {
        if (!shouldFilterImagesByColor) {
            const selectedVariantImageIndex = await getVariantImageIndex(variant);
            slideTo(selectedVariantImageIndex < 0 ? 0 : selectedVariantImageIndex);
        }
    };

    const handleVariantChange = (newVariantOption) => {
        const selectedVariant = getVariantByOptions(newVariantOption);
        setVariant(selectedVariant);
        slideToSelectedVariantImage(selectedVariant);
    };

    // its base64
    const getWarrantyTokenData = () => {
        if (typeof window === 'undefined') return null;
        const urlParams = new URLSearchParams(window.location.search);
        const warrantyToken = urlParams.get('warrantyToken');
        if (!warrantyToken) return null;

        try {
            const decodedToken = atob(warrantyToken);
            return JSON.parse(decodedToken);
        } catch (error) {
            console.error('Error decoding warranty token:', error);
            return {};
        }
    }

    const handleAddToCart = async (qty, additionalItems = [], customAttributes = []) => {
        const isValid = validateForm();

        if (product?.line_item?.id) {
            const warrantyData = getWarrantyTokenData();
            if (warrantyData) {
                customAttributes.push({ key: "__warranty_order_name", value: warrantyData.orderId });
                customAttributes.push({ key: "__warranty_line_item_id", value: product.line_item.id.split('/').pop() });
                customAttributes.push({ key: "__warranty_line_item_title", value: product.line_item.title });
                customAttributes.push({ key: "__warranty_line_item_category", value: product.line_item.customAttributes._item_category || 'Unknown' });
                customAttributes.push({ key: "__warranty_line_item_fits", value: product.line_item.customAttributes.FITS || 'Unknown' });
            }
        }

        if (!isValid) return;

        const filteredItems = additionalItems.length > 0 ? additionalItems?.filter((item, index) => index === additionalItems.findLastIndex((obj) => item.variantId === obj.variantId)) : [];
        const hasFits = customAttributes.find((attribute) => attribute.key === "FITS");

        const baseCustomAttributes = [...customAttributes];

        if (!hasFits && !isPatio) baseCustomAttributes.push({ key: "FITS", value: productData.version || productData?.product?.productType || productData?.product?.handle });
        if (productData?.version) baseCustomAttributes.push({ key: "Product Extra", value: productData.version });

        // ToDo: We are just using mapItems to get the item_variant for adding it to the customAttributes, we should refactor this
        // This call also populates the item_metrics in the productData object.. so thats why we get the correct data below..
        const productAnalytics = mapItems([{
            ...product,
            variant: product?.currentVariant?.node,
            quantity: qty,
            customAttributes: baseCustomAttributes,
        }]);
        if (productAnalytics?.[0]?.item_variant) {
            baseCustomAttributes.push({
                key: "_item_variant",
                value: productAnalytics[0].item_variant,
            });
        }
        // ----------------------------

        if (productData?.product?.item_metrics) {
            const itemMetrics = productData?.product?.item_metrics;
            // eslint-disable-next-line no-loops/no-loops
            for (let i = 1; i <= 5; i += 1) {
                const num = i === 1 ? "" : i;
                if (itemMetrics[`item_category${num}`]) {
                    baseCustomAttributes.push({
                        key: `_item_category${num}`,
                        value: `${itemMetrics[`item_category${num}`]}`,
                    });
                }
            }
        }

        await addCartItem([{
            variantId: product?.currentVariant?.node?.id,
            quantity: qty,
            customAttributes: baseCustomAttributes,
        }, ...filteredItems]);

        trackAddToCart({
            ...product,
            variant: product?.currentVariant?.node,
            quantity: qty,
            customAttributes: baseCustomAttributes,
        }, { prevUrl: currentLocation?.state?.prevUrl });

        navigate("/cart");
    };

    const handleQuantityChange = (value) => {
        // trackQuantityChange({
        //     ...product, variant: product?.currentVariant?.node, quantity: value,
        // }, value);
        setQuantity(value);
    };

    useEffect(() => {
        trackViewItem({ ...product, variant: product?.currentVariant?.node }, { prevUrl: currentLocation?.state?.prevUrl });
        NProgress.done();
    }, [product?.currentVariant]);

    const isVariantImage = (image) => product?.variants?.edges.some((variant) => variant?.node?.image?.id === image.id);

    const WarrantyBadge = () => {
        const findVehicleBadges = badgesByVehicles.find((badge) => product?.title?.toLowerCase().includes(badge.type));
        if (!findVehicleBadges) return null;

        const findVehicleWarranty = findVehicleBadges.badges.find((vehicleBadge) => product.title.toLowerCase().includes(vehicleBadge.key));
        if (!findVehicleWarranty) return null;

        return findVehicleWarranty.Component;
    };

    const [manualReview, setManualReview] = useState(null);

    useEffect(() => {
        const byStyleAndSize = manualReviews.find((badge) => {
            const titleMatch = product?.title?.toLowerCase().includes(badge.title);
            const handleMatchOneOf = badge?.handles?.some((handle) => product?.handle?.toLowerCase().includes(handle));
            return titleMatch && handleMatchOneOf;
        });
        if (byStyleAndSize) {
            setManualReview(byStyleAndSize);
            return;
        }

        const vehicleByType = manualReviews.find((badge) => product?.title?.toLowerCase().includes(badge.type));
        if (!vehicleByType) return;

        const vehicleReviews = vehicleByType?.reviews?.find((vehicleBadge) => product?.title?.toLowerCase().includes(vehicleBadge.key));
        if (!vehicleReviews) return;

        setManualReview(vehicleReviews);
    }, [product]);

    const isBoat = product?.title.toLowerCase().includes("boat cover");
    const isPontoon = product?.title.toLowerCase().includes("pontoon covers");
    const isJetSki = product?.title.toLowerCase().includes("jet ski covers");
    const isBimini = product?.title.toLowerCase().includes("bimini");
    const isCarSuvVanTruck = product?.title.toLowerCase().includes("car cover") || product?.title.toLowerCase().includes("suv cover") || product?.title.toLowerCase().includes("van cover") || product?.title.toLowerCase().includes("truck cover");

    const getFilteredImages = (images, selectedVariant) => {
        if (isPatio) return filterImagesByVariant(images, selectedVariant, product);
        if (isGroupedImages) return filterImagesByColor(images, selectedVariant, product);
        return images;
    };
    if (isPatio) {
        return (
            <div className="container pb-4 bg-white">
                <div className="">
                    <Breadcrumbs isCustomCover={true} items={breadcrumbItems} />
                </div>

                <PatioCalculator
                    productObject={product}
                    product={+extractGid(product?.id)}
                    variant={+extractGid(product?.currentVariant?.node?.id)}
                    parameters={defaultParameters}
                    customizations={defaultCustomizations}
                    initialConfiguration={initialConfiguration}
                    initialCustomAttributes={initialCustomAttributes}
                >

                    <div className="patio-product-page">
                        {useIsMobile() ? (
                            <>
                                <div className="patio-product-page__title-mobile">
                                    <h1 className="patio-product-page__title-main-mobile">{product?.title}</h1>
                                </div>
                                <a href="#reviews" className="product-item__rating" onClick={() => handleScrollToReviews()}>
                                    {Array.from({ length: 5 }).map((_, index) => (
                                        <svg width="22" height="22" viewBox="0 0 22 22" fill="#FFCB04" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11.1752 0.927734L13.6491 8.87578H21.6548L15.178 13.7879L17.652 21.736L11.1752 16.8238L4.69842 21.736L7.17232 13.7879L0.695555 8.87578H8.70128L11.1752 0.927734Z" fill="#FFCB04" />
                                        </svg>
                                    ))}
                                    <span className="patio-product-page__rating-reviews">{getReviewCounterForCustomProducts()} Reviews</span>
                                </a>
                            </>
                        ) : (null)}
                        <div className="patio-product-page__image-gallery">
                            <ImageGalleryPatio
                                images={getFilteredImages(
                                    product?.images?.edges,
                                    product?.currentVariant,
                                ).map((edge) => ({
                                    src: edge.node.url,
                                    title: edge.node.altText,
                                }))}
                                thumbsSlider={thumbsSlider}
                                setThumbsSlider={setThumbsSlider}
                                slideshowSlider={slideshowSlider}
                                setSlideshowSlider={setSlideshowSlider}
                                slideTo={slideTo}
                                measurements={product?.measurements}
                            />
                        </div>

                        <CausewayBridge product={product} />

                        <div className="patio-product-page__details">
                            {!useIsMobile() ? (
                                <>
                                    <div className="patio-product-page__title-desktop">
                                        <h1 className="patio-product-page__title-main-desktop">{product?.title}</h1>
                                    </div>
                                    <a href="#reviews" className="product-item__rating">
                                        {Array.from({ length: 5 }).map((_, index) => (
                                            <svg width="22" height="22" viewBox="0 0 22 22" fill="#FFCB04" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M11.1752 0.927734L13.6491 8.87578H21.6548L15.178 13.7879L17.652 21.736L11.1752 16.8238L4.69842 21.736L7.17232 13.7879L0.695555 8.87578H8.70128L11.1752 0.927734Z" fill="#FFCB04" />
                                            </svg>
                                        ))}
                                        <span className="patio-product-page__rating-reviews">{getReviewCounterForCustomProducts()} Reviews</span>
                                    </a>
                                </>

                            ) : (null)}
                            <Details
                                isPatio={isPatio}
                                product={product}
                                handleVariantChange={handleVariantChange}
                                quantity={quantity}
                                handleQuantityChange={handleQuantityChange}
                                handleAddToCart={handleAddToCart}
                                version={productData?.version}
                                patioConfig={patioConfig}
                                variantColors={variantColors}
                                isEditing={isEditing}
                            />
                        </div>

                        <div className="patio-product-page__summary">
                            <ProductSummary
                                item={item}
                                product={product}
                                quantity={quantity}
                                handleQuantityChange={handleQuantityChange}
                                onAddToCart={handleAddToCart}
                                isEditing={isEditing}
                                removeCartItem={removeCartItem}
                            />
                        </div>
                        <div className="patio-product-page__summary patio-product-page__summary-horizontal">
                            <ProductSummaryHorizontal
                                item={item}
                                product={product}
                                quantity={quantity}
                                handleQuantityChange={handleQuantityChange}
                                onAddToCart={handleAddToCart}
                                isEditing={isEditing}
                                removeCartItem={removeCartItem}
                            />
                        </div>

                    </div>

                </PatioCalculator>

                <div className="description-tabs description-tabs--patio ">
                    <Tabs>
                        <div title="Description">
                            {product?.description && (
                                <div
                                    className="pdp-description py-4 text-[#444444]"
                                >
                                    <ParsedContent content={product?.description} />
                                </div>
                            )}
                        </div>
                        <div title="FAQ">
                            {product?.faq && (
                                <div
                                    className="pdp-description py-4 text-[#444444]"
                                    dangerouslySetInnerHTML={{ __html: product?.faq }}
                                />
                            )}
                        </div>
                        <div title="Reviews">
                            <StoreReviews className="!p-0" />
                        </div>
                    </Tabs>

                </div>

            </div>
        );
    }

    return (
        <>
            <div className="container py-4 bg-white">
                <div className="flex lg:flex-row flex-col pb-10 border-b">
                    <div className="flex w-full lg:w-1/2">
                        <ImageGallery
                            slideshow={getFilteredImages(product?.images?.edges, product?.currentVariant)}
                            thumbnails={getFilteredImages(product?.images?.edges, product?.currentVariant)}
                            thumbsSlider={thumbsSlider}
                            setThumbsSlider={setThumbsSlider}
                            slideshowSlider={slideshowSlider}
                            setSlideshowSlider={setSlideshowSlider}
                            slideTo={slideTo}
                            initialSlide={initialGallerySlide}
                            onAfterSlideChange={() => {
                                // This was not being sent to GA4 anyways.. why do we need it?
                                // trackSliderClick({ ...product })
                            }}
                            imageAlt={getProductAttributes(product)}
                            isVariantImage={isVariantImage}
                            WarrantyBadge={isBoat || isJetSki || isPontoon || isCarSuvVanTruck || isBimini ? WarrantyBadge : null}
                        />
                    </div>

                    <div className="flex flex-col mt-5 lg:w-1/2 lg:pl-4 lg:m-0">
                        <Details
                            product={product}
                            handleVariantChange={handleVariantChange}
                            quantity={quantity}
                            handleQuantityChange={handleQuantityChange}
                            handleAddToCart={handleAddToCart}
                            version={productData?.version}
                            variantColors={variantColors}
                        />
                    </div>
                </div>
                {/* {video?.length > 0 && !isBoat && (
                    <div className="py-4 w-full border-b border-neutral-200 bg-white md:py-7">
                        <YoutubeFrame src={convertToEmbed(video[0].node.value)} className="w-full h-52 md:h-[22rem] lg:h-[28.25rem] xl:h-[40rem]" />
                    </div>
                )} */}

                {product?.description && <div className="pdp-description py-4 text-[#444444]" dangerouslySetInnerHTML={{ __html: product?.description }} />}
                {product?.bodyHtml && <div className="pdp-description py-4 text-[#444444]" dangerouslySetInnerHTML={{ __html: product?.bodyHtml }} />}
                {(["bow-boat-bimini-top", "bow-bimini-top", "bimini-top-replacement"].some((attribute) => product?.handle.toLowerCase().includes(attribute)) && product?.handle.toLowerCase().includes("fits")) || (product?.title.toLowerCase().includes("bimini top")) ? <img src="https://cdn.sanity.io/images/4swbd7aj/pre-prod/d81889ffb58811e7625a7f41b747a63ea83f40ce-1441x556.png" alt="Bimini Top Description" /> : null}

                {/* Added manual/hardcoded reviews */}
                {(product?.reviews && !manualReview) && <ProductReviews reviews={product?.reviews} />}
                {manualReview && <manualReview.Component {...manualReview.data} />}

            </div>
            <AffirmPayments price={product?.currentVariant?.node?.price} />
            <StoreReviews className="!p-0" />
        </>
    );
};

export default Product;
